import type { CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { PS, T } from '../../lib/theme';
import { formatClock, formatLocal } from '../../lib/time';
import type { Booking, PaymentStatus, Paged } from '../../lib/types';

function paymentStatusColor(status: PaymentStatus): string {
  if (status === 'confirmed') return PS.success;
  if (status === 'rejected' || status === 'expired') return PS.danger;
  return PS.textSec; // pending_*
}

interface Props {
  page: Paged<Booking>;
  onPageChange: (offset: number) => void;
  loading: boolean;
}

export function BookingsHistoryList({ page, onPageChange, loading }: Props) {
  const { items, total, limit, offset } = page;

  if (loading && items.length === 0) {
    return <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>;
  }
  if (items.length === 0) {
    return <div style={{ ...T.caption, color: PS.textSec }}>No bookings.</div>;
  }

  const showingFrom = offset + 1;
  const showingTo = Math.min(offset + items.length, total);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          background: PS.white,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
          overflowX: 'auto',
          // overflow-x:auto implicitly makes overflow-y `auto`, which traps
          // vertical page scroll on touch. Pin it to hidden (height fits).
          overflowY: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
              <th style={th}>Date</th>
              <th style={th}>Time</th>
              <th style={th}>Table</th>
              <th style={th}>Type</th>
              <th style={th}>Status</th>
              <th style={th}>Amount</th>
              <th style={th}>Method</th>
              <th style={th}>Payment</th>
              <th style={th}>Paid at</th>
              <th style={th}>Source</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const p = b.payments;
              return (
              <tr key={b.id} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                <td style={td}>{b.date}</td>
                <td style={td}>{formatClock(b.start_time)}–{formatClock(b.end_time)}</td>
                <td style={td}>{b.tables?.name ?? `#${b.table_id}`}</td>
                <td style={td}>{b.payment_type}</td>
                <td style={{ ...td, color: b.status === 'cancelled' ? PS.danger : PS.nearBlack }}>
                  {b.status}
                </td>
                {/* Payment/transaction details — null for subscription/credit bookings. */}
                <td style={td} title={p?.transaction_reference ?? undefined}>{p ? `₾${p.amount}` : '—'}</td>
                <td style={td}>{p?.method ?? '—'}</td>
                <td style={{ ...td, color: p ? paymentStatusColor(p.status) : PS.textSec }}>
                  {p?.status ?? '—'}
                </td>
                <td style={td}>{p?.paid_at ? formatLocal(p.paid_at, 'yyyy-MM-dd HH:mm') : '—'}</td>
                <td style={td}>{b.source}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ ...T.caption, color: PS.textSec }}>
          Showing {showingFrom}–{showingTo} of {total}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            disabled={offset === 0 || loading}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
          >
            ← Prev
          </Button>
          <Button
            variant="secondary"
            disabled={offset + items.length >= total || loading}
            onClick={() => onPageChange(offset + limit)}
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}

const th: CSSProperties = {
  padding: '10px 12px', textAlign: 'left',
  fontFamily: "'Barlow Condensed',sans-serif", fontSize: 12,
  letterSpacing: '1px', textTransform: 'uppercase', color: PS.textSec,
};
const td: CSSProperties = {
  padding: '10px 12px',
  fontFamily: "'Barlow',sans-serif", fontSize: 16, color: PS.nearBlack,
};
